
import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Autocomplete, Typography, Box, CircularProgress, Grid
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { collection, updateDoc, doc, Timestamp, addDoc } from 'firebase/firestore';
import { db } from '../../../firebase';
import useCustomerOptions from '../../../hooks/useCustomerOptions';
import { invalidateCollections } from '../../../hooks/useCachedCollection';
import { findDuplicateDeviceBySerial, duplicateDeviceMessage } from '../../../utils/deviceDedup';
import { logDeviceAction } from '../services/deviceLogs.service';
import { useAuth } from '../../../context/AuthContext';
import CustomerFormModal from '../../../components/modals/CustomerFormModal';
import { createCustomer } from '../../../services/customerService';

const DeviceTransferModal = ({ isOpen, onClose, device, currentCustomerId, onTransferSuccess }) => {
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [transferring, setTransferring] = useState(false);
    const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
    const { currentUser } = useAuth();

    // Müşteri listesi paylaşımlı oturum cache'inden gelir; bu modal her açılışında
    // tüm müşterileri (1324) yeniden okuyordu. Yeni müşteri eklendiğinde
    // refreshCustomers() ile liste tazelenir.
    const { customers: tumMusteriler, loading, refresh: refreshCustomers } = useCustomerOptions();

    const customers = React.useMemo(
        () => tumMusteriler.filter(c => c.id !== currentCustomerId),
        [tumMusteriler, currentCustomerId]
    );

    useEffect(() => {
        if (isOpen) setSelectedCustomer(null);
    }, [isOpen]);

    // Yeni müşteri ekleme handler'ı
    const handleAddNewCustomer = async (customerData, deviceInfo) => {
        try {
            // Mükerrer cihaz denetimi (normalize edilmiş) — müşteri oluşturulmadan önce.
            if (deviceInfo) {
                const duplicate = await findDuplicateDeviceBySerial(deviceInfo.seriNumarasi);
                if (duplicate) {
                    alert(duplicateDeviceMessage(duplicate, deviceInfo.seriNumarasi));
                    return;
                }
            }

            const customerId = await createCustomer(customerData);

            // Cihaz bilgisi varsa ekle
            if (deviceInfo) {
                const deviceData = {
                    ...deviceInfo,
                    musteriId: customerId,
                    musteriAdSoyad: customerData.adSoyad,
                    createdAt: Timestamp.now()
                };
                const deviceRef = await addDoc(collection(db, 'devices'), deviceData);
                await logDeviceAction(
                    deviceRef.id,
                    'create',
                    'Yeni cihaz oluşturuldu (Transfer sırasında müşteri oluşturulurken)',
                    currentUser?.uid || 'system',
                    deviceData
                );
            }

            // Müşteri listesini yenile ve yeni müşteriyi seç. Cihaz da oluşturulmuş
            // olabileceği için devices cache'ini de geçersiz kıl.
            await refreshCustomers();
            await invalidateCollections(['devices']);
            const newCustomer = { id: customerId, ...customerData };
            setSelectedCustomer(newCustomer);
            setIsCustomerModalOpen(false);
            alert('Müşteri başarıyla eklendi ve seçildi!');
        } catch (error) {
            console.error('Müşteri eklenirken hata:', error);
            alert('Müşteri eklenirken bir hata oluştu.');
        }
    };

    const handleTransfer = async () => {
        if (!selectedCustomer) return;
        setTransferring(true);
        try {
            const deviceRef = doc(db, 'devices', device.id);
            const oldData = { ...device };

            // Update Device
            await updateDoc(deviceRef, {
                musteriId: selectedCustomer.id,
                musteriAdSoyad: selectedCustomer.adSoyad,
                updatedAt: Timestamp.now()
            });

            // Log Action
            await logDeviceAction(
                device.id,
                'transfer',
                `Cihaz transfer edildi: ${oldData.musteriAdSoyad} -> ${selectedCustomer.adSoyad}`,
                currentUser?.uid || 'system',
                {
                    fromCustomerId: currentCustomerId,
                    toCustomerId: selectedCustomer.id,
                    fromCustomerName: oldData.musteriAdSoyad,
                    toCustomerName: selectedCustomer.adSoyad
                }
            );

            // Cihaz sahibi değişti; Cihazlar/Bakım/SGK dahil tüm açık cihaz listeleri
            // yeni sahibi göstersin diye cache'i geçersiz kıl.
            await invalidateCollections(['devices']);
            alert("Cihaz başarıyla transfer edildi!");
            onTransferSuccess();
            onClose();
        } catch (error) {
            console.error("Transfer error:", error);
            alert("Transfer sırasında hata oluştu.");
        } finally {
            setTransferring(false);
        }
    };

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>Cihaz Devret / Transfer Et</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" display="block">Transfer Edilecek Cihaz:</Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {device?.marka} {device?.model} - {device?.seriNumarasi}
                        </Typography>
                    </Box>

                    <Typography gutterBottom>Yeni Müşteri Seçiniz:</Typography>
                    {loading ? <CircularProgress size={20} /> : (
                        <Grid container spacing={1}>
                            <Grid item xs={9}>
                                <Autocomplete
                                    options={customers}
                                    getOptionLabel={(option) => `${option.adSoyad} (${option.telefon})`}
                                    value={selectedCustomer}
                                    onChange={(event, newValue) => setSelectedCustomer(newValue)}
                                    renderInput={(params) => <TextField {...params} label="Yeni Sahibi" placeholder="Müşteri ara..." />}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    startIcon={<PersonAddIcon />}
                                    onClick={() => setIsCustomerModalOpen(true)}
                                    sx={{ height: '56px' }}
                                >
                                    Yeni
                                </Button>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={transferring}>İptal</Button>
                    <Button
                        onClick={handleTransfer}
                        variant="contained"
                        color="warning"
                        disabled={!selectedCustomer || transferring}
                    >
                        {transferring ? 'Devrediliyor...' : 'Devret'}
                    </Button>
                </DialogActions>
            </Dialog>

            <CustomerFormModal
                isOpen={isCustomerModalOpen}
                onClose={() => setIsCustomerModalOpen(false)}
                onAddCustomer={handleAddNewCustomer}
                onUpdateCustomer={() => { }}
                customerToEdit={null}
            />
        </>
    );
};

export default DeviceTransferModal;
